import React, { useState, useEffect } from 'react';
import moment from "moment";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../css/styles.css';
import './addproject.css';
import DateRangePicker from "react-bootstrap-daterangepicker";
import Select from "react-select";
import axiosInstance from '../../services/AxiosInstance';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { hasFeatureAccess } from '../../utils/featureLock';
import '../Documents/commonstyle.css';

const AddProject = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  
  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    // Simple approach: try navigate(-1) and if it goes to login, redirect to project list
    navigate(-1);
    
    // Set a timeout to check if we ended up on login page
    setTimeout(() => {
      if (window.location.pathname.includes('/login')) {
        const search = searchParams.get('search') || '';
        navigate(`/task/projectlist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      }
    }, 100);
  };

  const [formData, setFormData] = useState({
    ProjectName: '',
    Description: '',
    StartDate: new Date(),
    EndDate: new Date(),
    createdBy: '',
    Status: null,
    Priority: '',
    OwnerId: null,
    Branches: [],
    Departments: [],
    SubDepartments: [],
    Members: [],
    Moderators: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [allCorporateUsers, setAllCorporateUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);

  // Fetch users by selected branch, department, sub-department (like TaskForm assignees/observers)
  const fetchUsersByFilters = async (branchIds, departmentIds, subDepartmentIds) => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData?.corporateId) return [];
      const params = new URLSearchParams();
      params.append('corporateId', parseInt(userData.corporateId));
      if (branchIds && branchIds.length > 0) params.append('branch', branchIds.join(','));
      if (departmentIds && departmentIds.length > 0) params.append('department', departmentIds.join(','));
      if (subDepartmentIds && subDepartmentIds.length > 0) params.append('sub_department', subDepartmentIds.join(','));
      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data?.users || [];
      return users.filter(user => (user.CorporateUserId ?? user.id) !== userData.id);
    } catch (error) {
      console.error('Error fetching users by filters:', error);
      return [];
    }
  };

  // When Branches, Departments, or SubDepartments change, refetch users and restrict Members/Moderators to that list
  useEffect(() => {
    if (!hasProjectAccess) return;
    let cancelled = false;
    const run = async () => {
      const branchIds = formData.Branches || [];
      const departmentIds = formData.Departments || [];
      const subDepartmentIds = formData.SubDepartments || [];
      const users = await fetchUsersByFilters(branchIds, departmentIds, subDepartmentIds);
      if (cancelled) return;
      setAllCorporateUsers(users);
      setAvailableAssignees(users);
      const hasFilter = branchIds.length > 0 || departmentIds.length > 0 || subDepartmentIds.length > 0;
      if (hasFilter) {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const currentUserId = userData?.id ?? userData?.CorporateUserId;
        const userIds = users.map(u => u.id ?? u.CorporateUserId);
        setFormData(prev => ({
          ...prev,
          Members: prev.Members.filter(m => userIds.includes(m.id ?? m.CorporateUserId)),
          Moderators: prev.Moderators.filter(m => userIds.includes(m.id ?? m.CorporateUserId)),
          OwnerId: prev.OwnerId != null && (userIds.includes(prev.OwnerId) || prev.OwnerId === currentUserId) ? prev.OwnerId : (userIds.length ? null : prev.OwnerId),
        }));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [formData.Branches, formData.Departments, formData.SubDepartments, hasProjectAccess]);

  // Check project access on component mount
  useEffect(() => {
    const checkProjectAccess = async () => {
      try {
        setFeatureLoading(true);
        const projectAccess = await hasFeatureAccess('project');
        setHasProjectAccess(projectAccess);
        
        if (!projectAccess) {
          setFeatureLoading(false);
          return;
        }
        
        const userData = JSON.parse(localStorage.getItem("user"));
        const corporateId = userData?.corporateId || userData?.id;
        try {
          const [branchRes, deptRes, subRes] = await Promise.all([
            axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`),
            axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`),
            axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`).catch(() =>
              axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`)
            ),
          ]);
          const branchList = branchRes.data?.data ?? branchRes.data ?? [];
          const deptList = deptRes.data?.data ?? deptRes.data ?? [];
          const subList = subRes.data?.data ?? subRes.data ?? [];
          setBranches(branchList);
          setDepartments(deptList);
          setSubDepartments(subList);
          setFilteredDepartments(deptList);
          setFilteredSubDepartments(subList);
        } catch (error) {
          notifyError(error);
        } finally {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking project access:', error);
        setHasProjectAccess(false);
      } finally {
        setFeatureLoading(false);
      }
    };
    
    checkProjectAccess();
  }, []);

  const notifyTopRight = (data) => {
    toast.success(`${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };
  const notifyError = (data) => {

    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const parsedUser = userData ? JSON.parse(userData) : null;
    const corporateId = parsedUser?.id ?? parsedUser?.CorporateUserId ?? null;
    setFormData((prev) => ({ ...prev, createdBy: corporateId, OwnerId: corporateId }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const isOnlySpecialChars = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return false;
    return !/[a-zA-Z0-9]/.test(trimmed);
  };

  const validation = () => {
    const errors = {};
    const now = new Date();
    const startDate = new Date(formData.StartDate);
    const endDate = new Date(formData.EndDate);

    if (!formData.ProjectName.trim()) {
      errors.ProjectName = 'Project Name is required';
    } else if (isOnlySpecialChars(formData.ProjectName)) {
      errors.ProjectName = 'Project name cannot contain only special characters.';
    }
    if (!formData.Description.trim()) {
      errors.Description = 'Description is required';
    }
    if (!formData.StartDate) {
      errors.StartDate = 'Start Date is required';
    }
    if (!formData.EndDate) {
      errors.EndDate = 'End Date is required';
    }

    setErrors(errors);

    // return true if no errors
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    const userData = JSON.parse(localStorage.getItem("user"))
    const params = new URLSearchParams();
    params.append('corporateId', parseInt(userData.corporateId))
    e.preventDefault();
    const payload = {
      ...formData,
      CorporateId: userData.corporateId,
      createdBy: formData.createdBy,
      StartDate: moment(formData.StartDate).format('YYYY-MM-DD'),
      EndDate: moment(formData.EndDate).format('YYYY-MM-DD'),
      Status: formData.Status ?? undefined,
      Priority: formData.Priority || undefined,
      OwnerId: formData.OwnerId ?? undefined,
      Branches: Array.isArray(formData.Branches) ? formData.Branches : [],
      Departments: Array.isArray(formData.Departments) ? formData.Departments : [],
      SubDepartments: Array.isArray(formData.SubDepartments) ? formData.SubDepartments : [],
      Members: JSON.stringify(formData.Members),
      Moderators: JSON.stringify(formData.Moderators),
    };
    if (validation()) {
      setIsSubmitting(true);
      setLoading(true);
      try {
        const response = await axiosInstance.post(`/project/add`, JSON.stringify(payload));

        if (!response) {
          throw new Error('Failed to submit the form');
        }

        const result = response;
        console.log('Form submitted successfully:', result);
        notifyTopRight('✅ Project created successfully!');
        setTimeout(() => {
          setIsSubmitting(false);
          navigateBackSafely();
        }, 2000);

        // Optional: Reset form after successful submission
        setFormData({
          ProjectName: '',
          Description: '',
          StartDate: '',
          EndDate: '',
          Priority: '',
          OwnerId: null,
          Branches: [],
          Departments: [],
          SubDepartments: [],
          Members: [],
          Moderators: [],
        });
        setErrors({});
      } catch (error) {
        console.error('Submission error:', error);
        notifyError(error);
      } finally {
        setLoading(false);
      }
    }
  };
  const memberOptions = availableAssignees
    .filter(
      (assignee) =>
        !formData.Members.some((m) => m.id === assignee.id)
    )
    .map((assignee) => ({
      value: assignee.id,
      label: assignee.full_name,
      assignee, // Keep full object for later use
    }));

  // Convert selected members into react-select format
  const selectedOptions = formData.Members.map((member) => {
    const user = availableAssignees.find((a) => a.id === member.id);
    return {
      value: member.id,
      label: user?.full_name || member.full_name,
    };
  });

  // Handler for selection
  const handleMembersChange = (selected) => {
    const members = selected.map((option) => {
      return availableAssignees.find((a) => a.id === option.value);
    });
    setFormData({ ...formData, Members: members });
  };

  const handleStatusChange = (selected) => {
    setFormData(prev => ({ ...prev, Status: selected?.value ?? selected }));
  };

  const priorityOptions = [
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
  ];
  const ownerOptions = (() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = userData?.id ?? userData?.CorporateUserId;
    const currentUserLabel = userData?.full_name || userData?.name || 'Me';
    const fromList = allCorporateUsers.map((u) => ({
      value: u.id ?? u.CorporateUserId,
      label: u.full_name || u.name || `User ${u.id}`,
    }));
    const hasCurrentUser = fromList.some((o) => o.value === currentUserId);
    if (currentUserId != null && !hasCurrentUser) {
      return [{ value: currentUserId, label: currentUserLabel }, ...fromList];
    }
    return fromList;
  })();
  const branchOptions = branches.map((b) => ({ value: b.id, label: b.name || b.branch_name || String(b.id) }));
  const departmentsForSelect = filteredDepartments.length > 0 ? filteredDepartments : departments;
  const departmentOptions = departmentsForSelect.map((d) => ({ value: d.id, label: d.name || d.department_name || String(d.id) }));
  const getSubDeptId = (sd) => sd.department_id ?? sd.DepartmentId ?? sd.departmentId;
  const subDepartmentsForSelect =
    (formData.Departments?.length > 0)
      ? filteredSubDepartments
      : (filteredSubDepartments.length > 0 ? filteredSubDepartments : subDepartments);
  const subDepartmentOptions = subDepartmentsForSelect.map((s) => ({ value: s.id, label: s.name || s.sub_department_name || String(s.id) }));

  const handleBranchChange = async (selected) => {
    const branchIds = (selected || []).map((s) => s.value);
    setFormData((prev) => ({ ...prev, Branches: branchIds }));
    if (branchIds.length === 0) {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
      setFormData((prev) => ({ ...prev, Departments: [], SubDepartments: [] }));
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const deptPromises = branchIds.map((branchId) =>
        axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${branchId}`)
      );
      const deptResponses = await Promise.all(deptPromises);
      const allDepts = [];
      deptResponses.forEach((res) => {
        const data = res.data?.data ?? res.data ?? [];
        data.forEach((d) => {
          if (!allDepts.some((x) => x.id === d.id)) allDepts.push(d);
        });
      });
      setFilteredDepartments(allDepts);
      const validDeptIds = allDepts.map((d) => d.id);
      setFormData((prev) => ({
        ...prev,
        Departments: (prev.Departments || []).filter((id) => validDeptIds.includes(id)),
        SubDepartments: [],
      }));

      const subPromises = branchIds.map((branchId) =>
        axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}&branch_id=${branchId}`).catch(() =>
          axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}&branch_id=${branchId}`)
        )
      );
      const subResponses = await Promise.all(subPromises);
      const allSubs = [];
      subResponses.forEach((res) => {
        const data = res.data?.data ?? res.data ?? [];
        data.forEach((sd) => {
          if (!allSubs.some((x) => x.id === sd.id)) allSubs.push(sd);
        });
      });
      const subsForBranches = allSubs.filter((sd) => validDeptIds.includes(getSubDeptId(sd)));
      setFilteredSubDepartments(subsForBranches);
    } catch (err) {
      console.error('Error fetching departments by branch:', err);
      setFilteredDepartments(departments.filter((d) => branchIds.includes(d.branch_id)));
      setFilteredSubDepartments(subDepartments.filter((sd) => {
        const dept = departments.find((d) => d.id === getSubDeptId(sd));
        return dept && branchIds.includes(dept.branch_id);
      }));
    }
  };

  const handleDepartmentChange = async (selected) => {
    const departmentIds = (selected || []).map((s) => s.value);
    const branchIds = formData.Branches || [];
    setFormData((prev) => ({ ...prev, Departments: departmentIds }));
    if (departmentIds.length === 0) {
      if (branchIds.length > 0) {
        try {
          const userData = JSON.parse(localStorage.getItem('user'));
          const corporateId = userData?.corporateId || userData?.id;
          const subPromises = branchIds.map((branchId) =>
            axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}&branch_id=${branchId}`).catch(() =>
              axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}&branch_id=${branchId}`)
            )
          );
          const subResponses = await Promise.all(subPromises);
          const allSubs = [];
          subResponses.forEach((res) => {
            const data = res.data?.data ?? res.data ?? [];
            data.forEach((sd) => {
              if (!allSubs.some((x) => x.id === sd.id)) allSubs.push(sd);
            });
          });
          const validDeptIds = (filteredDepartments.length > 0 ? filteredDepartments : departments).map((d) => d.id);
          setFilteredSubDepartments(allSubs.filter((sd) => validDeptIds.includes(getSubDeptId(sd))));
        } catch (e) {
          setFilteredSubDepartments([]);
        }
      } else {
        setFilteredSubDepartments(subDepartments);
      }
      setFormData((prev) => ({ ...prev, SubDepartments: [] }));
      return;
    }
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      let subPromises = [];
      if (branchIds.length > 0) {
        departmentIds.forEach((departmentId) => {
          branchIds.forEach((branchId) => {
            const url = `/settings/subDepartments?corporate_id=${corporateId}&department_id=${departmentId}&branch_id=${branchId}`;
            subPromises.push(
              axiosInstance.get(url).catch(() => axiosInstance.get(url.replace('/settings/subDepartments', '/settings/subdepartments')))
            );
          });
        });
      } else {
        departmentIds.forEach((departmentId) => {
          const url = `/settings/subDepartments?corporate_id=${corporateId}&department_id=${departmentId}`;
          subPromises.push(
            axiosInstance.get(url).catch(() => axiosInstance.get(url.replace('/settings/subDepartments', '/settings/subdepartments')))
          );
        });
      }
      const subResponses = await Promise.all(subPromises);
      const allSubs = [];
      subResponses.forEach((res) => {
        const data = res.data?.data ?? res.data ?? [];
        const list = Array.isArray(data) ? data : [];
        list.forEach((sd) => {
          if (!allSubs.some((x) => x.id === sd.id)) allSubs.push(sd);
        });
      });
      const subs = allSubs.filter((sd) => departmentIds.includes(getSubDeptId(sd)));
      if (subs.length === 0) {
        const source = filteredSubDepartments.length > 0 ? filteredSubDepartments : subDepartments;
        const fallback = source.filter((sd) => departmentIds.includes(getSubDeptId(sd)));
        setFilteredSubDepartments(fallback);
        setFormData((prev) => ({
          ...prev,
          SubDepartments: (prev.SubDepartments || []).filter((id) => fallback.some((s) => (s.id ?? s.sub_department_id) === id)),
        }));
      } else {
        setFilteredSubDepartments(subs);
        setFormData((prev) => ({
          ...prev,
          SubDepartments: (prev.SubDepartments || []).filter((id) => subs.some((s) => s.id === id)),
        }));
      }
    } catch (err) {
      console.error('Error fetching sub-departments by department:', err);
      const fallback = subDepartments.filter((sd) => departmentIds.includes(getSubDeptId(sd)));
      setFilteredSubDepartments(fallback);
      setFormData((prev) => ({
        ...prev,
        SubDepartments: (prev.SubDepartments || []).filter((id) => fallback.some((s) => (s.id ?? s.sub_department_id) === id)),
      }));
    }
  };
  const moderatorOptions = availableAssignees
    .filter(
      (mod) =>
        !formData.Moderators.some((m) => m.id === mod.id)
    )
    .map((mod) => ({
      value: mod.id,
      label: mod.full_name,
      mod, // store full object if needed
    }));
  const selectedModeratorOptions = formData.Moderators.map((mod) => {
    const user = availableAssignees.find((a) => a.id === mod.id);
    return {
      value: mod.id,
      label: user?.full_name,
    };
  });
  const handleModeratorsChange = (selected) => {
    const moderators = selected.map((option) => {
      return availableAssignees.find((a) => a.id === option.value);
    });
    setFormData({ ...formData, Moderators: moderators });
  };
  // Handle the date range selection
  const handleDateChange = ({ startDate, endDate }) => {
    console.log(startDate, endDate)
    setFormData((prevData) => ({
      ...prevData,
      startDate,
      endDate,
    }));
  };

  // Show loading screen while checking feature access
  if (featureLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show feature locked message if user doesn't have project access
  if (!hasProjectAccess) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="feature-locked">
                    <i className="fas fa-lock" style={{ fontSize: '3rem', marginBottom: '1rem', color: '#c42126' }} aria-hidden="true"></i>
                    <h4 className="text-muted mb-3">Project Management Not Available</h4>
                    <p className="text-muted mb-4">
                      Project management is not available in your current subscription plan.
                      Upgrade your plan to access this feature.
                    </p>
                    <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                      <button
                        type="button"
                        className="btn doc-form-cancel-btn"
                        onClick={() => navigate('/task/projectlist')}
                      >
                        <i className="fas fa-arrow-left me-2" aria-hidden="true"></i>
                        Back to Project List
                      </button>
                      <button
                        type="button"
                        className="btn doc-form-submit-btn"
                        onClick={() => navigate('/user-subscription')}
                      >
                        <i className="fas fa-arrow-right me-2" aria-hidden="true"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <ToastContainer />
      {console.log(formData)}
      <div className="col-xl-12 col-xxl-12" style={{ height: 'fit-content' }}>
        <button
          className="back-button"
          onClick={() => {
            navigateBackSafely();
          }}
        >
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
        <div className="card">
          <div className="card-body">
            <section>
              <div className="row">
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Project Name*</label>
                    <input
                      type="text"
                      name="ProjectName"
                      className="form-control"
                      placeholder=""
                      required
                      value={formData.ProjectName}
                      onChange={handleChange}
                    />
                    {errors.ProjectName && <div className="text-danger">{errors.ProjectName}</div>}
                  </div>
                </div>
                <div className="form-group col-lg-6 mb-3">
                  <div style={{ marginBottom: '15px' }}>
                    <label className='text-label' style={{ marginBottom: '0.8rem' }}>Status</label>
                    <Select
                      options={[{ value: 1, label: 'Pending' }, { value: 2, label: 'In Progress' }, { value: 3, label: 'Completed' }]}
                      value={formData.Status != null ? { value: formData.Status, label: formData.Status === 1 ? 'Pending' : formData.Status === 2 ? 'In Progress' : 'Completed' } : null}
                      onChange={(selected) => handleStatusChange(selected)}
                      name="status"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          boxShadow: '#C4282170',
                          borderRadius: '5px',
                          '&:hover': {
                            borderColor: '#C42821',
                            boxShadow: '#C4282170',
                            borderRadius: '5px',
                          },
                          menu: (base) => ({
                            ...base,
                            borderRadius: '5px',
                          })
                        }),
                      }}
                    />
                    {errors.status && <div style={{ color: 'red' }}>{errors.status}</div>}
                  </div>

                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Description*</label>
                    <div className="form-group ">
                      <textarea
                        className="project_desc"
                        rows="4"
                        id="Description"
                        name='Description'
                        value={formData.Description}
                        onChange={handleChange}
                      ></textarea>
                      {errors.Description && <div className="text-danger">{errors.Description}</div>}
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">StartDate-EndDate</label>
                    <div className="form-group ">
                      <div className="form-group">
                        <DateRangePicker
                          initialSettings={{
                            startDate: new Date(),
                            endDate: new Date(),
                            locale: { format: 'MM/DD/YYYY' },
                          }}
                          onCallback={(start, end) => {
                            setFormData((prev) => ({
                              ...prev,
                              StartDate: start.toISOString(), // or start.format('YYYY-MM-DD') if needed
                              EndDate: end.toISOString(),
                            }));
                          }}
                        >
                          <input
                            type="text"
                            className="form-control input-daterange-timepicker"
                            readOnly
                          />
                        </DateRangePicker>
                        {errors.StartDate && <div className="text-danger">{errors.StartDate}</div>}
                        {errors.EndDate && <div className="text-danger">{errors.EndDate}</div>}
                      </div>

                    </div>
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Priority</label>
                    <Select
                      options={priorityOptions}
                      value={formData.Priority ? priorityOptions.find(o => o.value === formData.Priority) : null}
                      onChange={(selected) => setFormData(prev => ({ ...prev, Priority: selected?.value ?? '' }))}
                      isClearable
                      placeholder="Select priority"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '5px',
                          '&:hover': { borderColor: '#C42821', borderRadius: '5px' },
                        }),
                        menu: (base) => ({ ...base, borderRadius: '5px' }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Owner</label>
                    <Select
                      options={ownerOptions}
                      value={formData.OwnerId != null ? ownerOptions.find(o => o.value === formData.OwnerId) : null}
                      onChange={(selected) => setFormData(prev => ({ ...prev, OwnerId: selected?.value ?? null }))}
                      isClearable
                      placeholder="Select owner (corporate user)"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '5px',
                          '&:hover': { borderColor: '#C42821', borderRadius: '5px' },
                        }),
                        menu: (base) => ({ ...base, borderRadius: '5px' }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Branches</label>
                    <Select
                      isMulti
                      options={branchOptions}
                      value={branchOptions.filter(o => (formData.Branches || []).includes(o.value))}
                      onChange={handleBranchChange}
                      placeholder="Select branches"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '5px',
                          '&:hover': { borderColor: '#C42821', borderRadius: '5px' },
                        }),
                        menu: (base) => ({ ...base, borderRadius: '5px' }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Departments</label>
                    <Select
                      isMulti
                      options={departmentOptions}
                      value={departmentOptions.filter(o => (formData.Departments || []).includes(o.value))}
                      onChange={handleDepartmentChange}
                      placeholder="Select departments"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '5px',
                          '&:hover': { borderColor: '#C42821', borderRadius: '5px' },
                        }),
                        menu: (base) => ({ ...base, borderRadius: '5px' }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Sub-departments</label>
                    <Select
                      isMulti
                      options={subDepartmentOptions}
                      value={subDepartmentOptions.filter(o => (formData.SubDepartments || []).includes(o.value))}
                      onChange={(selected) => setFormData(prev => ({ ...prev, SubDepartments: (selected || []).map(s => s.value) }))}
                      placeholder="Select sub-departments"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '5px',
                          '&:hover': { borderColor: '#C42821', borderRadius: '5px' },
                        }),
                        menu: (base) => ({ ...base, borderRadius: '5px' }),
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <div style={{ marginBottom: '15px' }}>
                      <label className='text-label' style={{ marginBottom: '0.8rem' }}>Members</label>
                      <Select
                        isMulti
                        options={memberOptions}
                        value={selectedOptions}
                        onChange={handleMembersChange}
                        name="Members"
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            boxShadow: '#C4282170',
                            borderRadius: '5px',
                            '&:hover': {
                              borderColor: '#C42821',
                              borderColor: '#C4282170',
                              borderRadius: '5px',
                            },
                            menu: (base) => ({
                              ...base,
                              borderRadius: '5px',
                            })
                          }),
                        }}
                      />
                      {errors.Members && <div style={{ color: 'red' }}>{errors.Members}</div>}
                    </div>

                  </div>
                </div>
                <div className="col-lg-6 mb-2">
                  <div className="form-group mb-3">
                    <label className="text-label">Moderators</label>
                    <Select
                      isMulti
                      options={moderatorOptions}
                      value={selectedModeratorOptions}
                      onChange={handleModeratorsChange}
                      name="Members"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          boxShadow: '#C4282170',
                          borderRadius: '5px',
                          '&:hover': {
                            borderColor: '#C42821',
                            boxShadow: '#C4282170',
                            borderRadius: '5px',
                          },
                          menu: (base) => ({
                            ...base,
                            borderRadius: '5px',
                          })
                        }),
                      }} />



                    {errors.Moderators && <div className="text-danger">{errors.Moderators}</div>}
                  </div>
                </div>
                <div className="text-end toolbar toolbar-bottom p-2">
                  <button 
                    type="button"
                    className="btn btn-cancel me-2" 
                    onClick={navigateBackSafely}
                    disabled={isSubmitting}
                    style={{ maxWidth: '100px' }}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary sw-btn-next" onClick={handleSubmit} disabled={isSubmitting} style={{ maxWidth: '100px' }}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : null}
                    Submit
                  </button>
                </div>

              </div>
            </section>
          </div>
        </div>
      </div>
      <InternalFooter />
    </div>
  )
}
export default AddProject;